"use client";

import Link from "next/link";
import { useLocale, useTranslations } from "next-intl";
import { useGetAdminDashboard } from "@/generated/api/admin/admin";
import { formatDecimal, formatMoney } from "@/lib/format";
import { AdminIcon, type AdminIconName } from "./AdminIcon";
import { useAdminPageTitle } from "./AdminDocumentTitle";

type DashboardMetricProps = {
  description: string;
  icon: AdminIconName;
  label: string;
  tone?: "default" | "positive" | "warning" | "brand";
  value: string;
};

function DashboardMetric({ description, icon, label, tone = "default", value }: DashboardMetricProps) {
  return (
    <article className={`dashboard-metric dashboard-metric--${tone}`}>
      <span className="dashboard-metric__icon"><AdminIcon name={icon} /></span>
      <span className="dashboard-metric__label">{label}</span>
      <strong>{value}</strong>
      <small>{description}</small>
    </article>
  );
}

export function AdminDashboardPage() {
  const t = useTranslations("dashboard");
  const commonT = useTranslations("common");
  const locale = useLocale();
  const dashboardQuery = useGetAdminDashboard();
  const dashboard = dashboardQuery.data;
  useAdminPageTitle(t("title"));

  if (dashboardQuery.isLoading) {
    return (
      <div className="dashboard-loading" aria-label={commonT("loading")} role="status">
        <span />
        <span />
        <span />
      </div>
    );
  }

  if (dashboardQuery.isError || !dashboard) {
    return (
      <section className="page-state page-state--error">
        <h2>{commonT("errorTitle")}</h2>
        <p>{commonT("errorText")}</p>
        <button className="secondary-button" onClick={() => void dashboardQuery.refetch()} type="button">
          {commonT("retry")}
        </button>
      </section>
    );
  }

  const activeRate = dashboard.platform.totalPartners > 0
    ? Math.round((dashboard.platform.activePartners / dashboard.platform.totalPartners) * 100)
    : 0;
  const annualRecurringRevenue = dashboard.platform.annualRecurringRevenue;

  return (
    <div className="dashboard-page page-stack">
      <header className="dashboard-hero">
        <div className="dashboard-hero__copy">
          <p>{t("subtitle")}</p>
          <Link className="dashboard-hero__action" href="/partners">
            {t("partnersAction")}
            <AdminIcon name="arrow-right" />
          </Link>
        </div>

        <div className="dashboard-health">
          <span className="dashboard-health__icon"><AdminIcon name="activity" /></span>
          <span>{t("healthLabel")}</span>
          <strong>
            {formatDecimal(dashboard.platform.activePartners, locale)}
            <small>/ {formatDecimal(dashboard.platform.totalPartners, locale)}</small>
          </strong>
          <div className="dashboard-health__track" aria-hidden="true">
            <i style={{ width: `${activeRate}%` }} />
          </div>
          <p>{t("healthRate", { rate: formatDecimal(activeRate, locale) })}</p>
        </div>

        <div className="dashboard-hero__orbit" aria-hidden="true"><i /><i /><i /></div>
      </header>

      <section className="dashboard-section" aria-labelledby="dashboard-arr-title">
        <div className="dashboard-section__heading">
          <div>
            <h2 id="dashboard-arr-title">{t("arrTitle")}</h2>
          </div>
          <span>{t("arrFormula")}</span>
        </div>
        <article className="dashboard-revenue-card">
          <span className="dashboard-revenue-card__icon"><AdminIcon name="subscription" /></span>
          <span>{t("arrLabel")}</span>
          <strong>
            {annualRecurringRevenue
              ? formatMoney(annualRecurringRevenue.amount, locale, annualRecurringRevenue.currency)
              : t("arrUnavailable")}
          </strong>
          <small>{annualRecurringRevenue ? t("arrHint") : t("arrUnavailableHint")}</small>
        </article>
      </section>

      <section className="dashboard-section" aria-labelledby="dashboard-platform-title">
        <div className="dashboard-section__heading">
          <div>
            <h2 id="dashboard-platform-title">{t("platformTitle")}</h2>
          </div>
          <span><AdminIcon name="calendar" />{t("window")}</span>
        </div>
        <div className="dashboard-metric-grid">
          <DashboardMetric
            description={t("totalPartnersHint")}
            icon="partners"
            label={t("totalPartners")}
            tone="brand"
            value={formatDecimal(dashboard.platform.totalPartners, locale)}
          />
          <DashboardMetric
            description={t("activePartnersHint")}
            icon="activity"
            label={t("activePartners")}
            tone="positive"
            value={formatDecimal(dashboard.platform.activePartners, locale)}
          />
          <DashboardMetric
            description={t("inactivePartnersHint")}
            icon="shield"
            label={t("inactivePartners")}
            tone="warning"
            value={formatDecimal(dashboard.platform.inactivePartners, locale)}
          />
          <DashboardMetric
            description={t("newPartnersHint")}
            icon="partners"
            label={t("newPartnersLast30Days")}
            value={formatDecimal(dashboard.platform.newPartnersLast30Days, locale)}
          />
        </div>
      </section>

      <section className="dashboard-section" aria-labelledby="dashboard-business-title">
        <div className="dashboard-section__heading">
          <div>
            <h2 id="dashboard-business-title">{t("businessTitle")}</h2>
          </div>
          <span>{t("businessSubtitle")}</span>
        </div>
        <div className="dashboard-business-grid">
          <article className="dashboard-revenue-card">
            <span className="dashboard-revenue-card__icon"><AdminIcon name="revenue" /></span>
            <span>{t("revenueLast30Days")}</span>
            <strong>{formatMoney(dashboard.business.revenueLast30Days, locale)}</strong>
            <small>{t("revenueHint")}</small>
            <div aria-hidden="true"><i /><i /><i /><i /><i /><i /><i /></div>
          </article>
          <DashboardMetric
            description={t("ordersHint")}
            icon="orders"
            label={t("ordersLast30Days")}
            value={formatDecimal(dashboard.business.ordersLast30Days, locale)}
          />
          <DashboardMetric
            description={t("leadsHint")}
            icon="leads"
            label={t("leadsLast30Days")}
            value={formatDecimal(dashboard.business.leadsLast30Days, locale)}
          />
        </div>
      </section>
    </div>
  );
}
